import React, { useState, useEffect } from "react";
import axios from "axios";
import { Spin, Alert, Card, Typography, List, Button, Row, Col } from "antd";
import { useNavigate } from "react-router-dom";
const { Title, Text } = Typography;
const PersonalityAssessmentReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const quadrantMap = {
    1: {
      label: "DOMINANCE (D)",
      color: "#E74C3C",
      bgColor: "rgba(231, 76, 60, 0.05)",
      traits: ["Focused", "Inspirational", "Independent"],
    },
    2: {
      label: "INFLUENCE (I)",
      color: "#2ECC71",
      bgColor: "rgba(46, 204, 113, 0.05)",
      traits: ["Energetic", "Sociable", "Positive"],
    },
    3: {
      label: "STEADINESS (S)",
      color: "#3498DB",
      bgColor: "rgba(52, 152, 219, 0.05)",
      traits: ["Patience", "Persistence", "Thoughtfulness"],
    },
    4: {
      label: "COMPLIANCE (C)",
      color: "#9A6B0E",
      bgColor: "rgba(154, 107, 14, 0.05)",
      traits: ["Structure", "Organization", "Data-driven"],
    },
  };
  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      const learnerId = localStorage.getItem("personalId");
      if (!token || !learnerId) {
        throw new Error("Authentication required. Please login again.");
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/PersonalityAssessment/Learner/Report`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            learnerId: parseInt(learnerId)
          }
        }
      );
      if (!response.data?.report) {
        throw new Error("No report data found in response");
      }
      setReport(response.data.report);
    } catch (error) {
      console.error("Error fetching report:", error);
      let errorMessage = "Failed to load personality assessment report";
      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = "Invalid request. Please check your data.";
            break;
          case 401:
            errorMessage = "Session expired. Please login again.";
            break;
          case 403:
            errorMessage = "You don't have permission to access this report.";
            break;
          case 404:
            errorMessage = "No report found. Please complete the assessment first.";
            break;
          default:
            errorMessage = error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = error.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchReport();
  }, []);
  const handleRetry = () => {
    fetchReport();
  };
  const handleTakeAssessment = () => {
    navigate("/personality-assessment");
  };
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <Spin size="large" tip="Loading your personality report..." />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
        <Alert
          type="error"
          message="Error Loading Report"
          description={
            <div>
              <p>{error}</p>
              <div style={{ marginTop: "16px" }}>
                {error.includes("expired") || error.includes("required") ? (
                  <Button type="primary" onClick={() => navigate("/login")}>
                    Go to Login
                  </Button>
                ) : error.includes("No report found") ? (
                  <Button type="primary" onClick={handleTakeAssessment}>
                    Take Assessment
                  </Button>
                ) : (
                  <Button type="primary" onClick={handleRetry}>
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          }
          showIcon
        />
      </div>
    );
  }
  if (!report) {
    return (
      <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
        <Card>
          <Text type="secondary">No personality assessment report available.</Text>
          <div style={{ marginTop: "16px" }}>
            <Button type="primary" onClick={handleTakeAssessment}>
              Take Personality Assessment
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  return (
    <Card
      className="card-professional"
      title={`DISC Personality Report: ${report?.displayName} (${report?.typeCode})`}
      loading={loading}
      variant="outlined"
      extra={
        <Button type="default" onClick={() => navigate(-1)}>
          Back
        </Button>
      }
    >
      <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
        <Card style={{ marginBottom: 24 }}>
          <Text>{report.summary}</Text>
        </Card>
        <Title level={4}>Key Traits</Title>
        <List
          bordered
          dataSource={report.keyTraits}
          renderItem={(trait) => <List.Item>{trait}</List.Item>}
          style={{ marginBottom: 24 }}
        />
        <Title level={4}>Strengths</Title>
        <List
          bordered
          dataSource={report.strengths}
          renderItem={(strength) => (
            <List.Item>
              <Text strong>{strength.title}:</Text> {strength.description}
            </List.Item>
          )}
          style={{ marginBottom: 24 }}
        />
        <Title level={4}>Development Tips</Title>
        <List
          bordered
          dataSource={report.developmentTips}
          renderItem={(tip) => (
            <List.Item>
              <Text strong>{tip.title}:</Text> {tip.description}
            </List.Item>
          )}
          style={{ marginBottom: 24 }}
        />
        <div style={{ background: "#fff", padding: "24px", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 32 }}>
          <h2 style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 16 }}>DISC Quadrant Traits</h2>
          <div
            style={{
              width: "100%",
              maxWidth: "600px",
              aspectRatio: "1 / 1",
              margin: "0 auto",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1fr 1fr",
            }}
          >
            {report.quadrantScores?.sort((a, b) => a.quadrant - b.quadrant).map((q) => {
              const quadrant = quadrantMap[q.quadrant];
              return (
                <div
                  key={q.quadrant}
                  style={{
                    borderRight: [1, 3].includes(q.quadrant) ? "1px solid #ccc" : undefined,
                    borderBottom: [1, 2].includes(q.quadrant) ? "1px solid #ccc" : undefined,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "16px",
                    backgroundColor: quadrant.bgColor,
                  }}
                >
                  <h5 style={{ color: quadrant.color, marginBottom: 4 }}>{quadrant.label}</h5>
                  <h6 style={{ fontWeight: "bold" }}>{q.scorePercentage}%</h6>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, textAlign: "center", color: "rgba(0, 0, 0, 0.45)", fontSize: 12 }}>
                    {quadrant.traits.map((trait) => (
                      <li key={trait}>{trait}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ background: "#fff", padding: "24px", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 24 }}>
          <h2 style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 8 }}>DISC Personality Model</h2>
          <p>To help you understand the DISC model of personality here are definitions of the four factors measured.</p>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ padding: 16, background: "#fafafa", borderRadius: 4, borderLeft: "3px solid #E74C3C" }}>
                <h5 style={{ color: "#E74C3C" }}>Dominance</h5>
                <p>Direct, results-oriented, competitive. Handles problems assertively.</p>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ padding: 16, background: "#fafafa", borderRadius: 4, borderLeft: "3px solid #2ECC71" }}>
                <h5 style={{ color: "#2ECC71" }}>Influence</h5>
                <p>Outgoing, enthusiastic, optimistic. Strong communication and relational skills.</p>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ padding: 16, background: "#fafafa", borderRadius: 4, borderLeft: "3px solid #3498DB" }}>
                <h5 style={{ color: "#3498DB" }}>Steadiness</h5>
                <p>Calm, patient, and dependable. Handles tasks thoughtfully.</p>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ padding: 16, background: "#fafafa", borderRadius: 4, borderLeft: "3px solid #9A6B0E" }}>
                <h5 style={{ color: "#9A6B0E" }}>Compliance</h5>
                <p>Analytical, precise, systematic. Values structure and rules.</p>
              </div>
            </Col>
          </Row>
        </div>

      </div>
    </Card>
  );
};
export default PersonalityAssessmentReport;
